// ProductSalesReport.js - Statistik over salda produkter

import React, { useState, useEffect, useCallback } from 'react';
import { getProductStatistics } from './calendarApi';
import './ProductSalesReport.css';

const ProductSalesReport = () => {
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProductStatistics(fromDate, toDate);
      setReport(data);
    } catch (err) {
      console.error('Error loading product statistics:', err);
      setError('Kunde inte ladda produktstatistik');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const setQuickPeriod = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setFromDate(start.toISOString().split('T')[0]);
    setToDate(end.toISOString().split('T')[0]);
  };

  const setThisMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setFromDate(start.toISOString().split('T')[0]);
    setToDate(now.toISOString().split('T')[0]);
  };

  const setLastMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    setFromDate(start.toISOString().split('T')[0]);
    setToDate(end.toISOString().split('T')[0]);
  };

  if (loading && !report) {
    return <div className="psr-loading">Laddar produktstatistik...</div>;
  }

  return (
    <div className="product-sales-report">
      <div className="psr-header">
        <h2><i className="fas fa-chart-bar"></i> Produktstatistik</h2>

        <div className="psr-controls">
          <div className="psr-date-range">
            <label>Fran:</label>
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="psr-date-input"
            />
            <label>Till:</label>
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="psr-date-input"
            />
          </div>

          <div className="psr-quick-periods">
            <button onClick={() => setQuickPeriod(7)} className="quick-btn">7 dagar</button>
            <button onClick={() => setQuickPeriod(30)} className="quick-btn">30 dagar</button>
            <button onClick={() => setQuickPeriod(90)} className="quick-btn">90 dagar</button>
            <button onClick={setThisMonth} className="quick-btn">Denna manad</button>
            <button onClick={setLastMonth} className="quick-btn">Forra manaden</button>
          </div>
        </div>
      </div>

      {error && <div className="psr-error">{error}</div>}

      {report && (
        <>
          {/* Sammanfattningskort */}
          <div className="psr-summary">
            <div className="psr-summary-card total">
              <div className="psr-card-icon">
                <i className="fas fa-boxes"></i>
              </div>
              <div className="psr-card-content">
                <span className="psr-card-label">Antal salda</span>
                <span className="psr-card-value">{report.summary.total_products_sold}</span>
              </div>
            </div>

            <div className="psr-summary-card revenue">
              <div className="psr-card-icon">
                <i className="fas fa-coins"></i>
              </div>
              <div className="psr-card-content">
                <span className="psr-card-label">Total forsaljning</span>
                <span className="psr-card-value">{report.summary.total_revenue.toFixed(2)} kr</span>
              </div>
            </div>

            <div className="psr-summary-card vat">
              <div className="psr-card-icon">
                <i className="fas fa-percent"></i>
              </div>
              <div className="psr-card-content">
                <span className="psr-card-label">Total moms</span>
                <span className="psr-card-value">{report.summary.total_vat.toFixed(2)} kr</span>
              </div>
            </div>

            {report.summary.friskvard_revenue > 0 && (
              <div className="psr-summary-card friskvard">
                <div className="psr-card-icon">
                  <i className="fas fa-heart"></i>
                </div>
                <div className="psr-card-content">
                  <span className="psr-card-label">Friskvard</span>
                  <span className="psr-card-value">{report.summary.friskvard_revenue.toFixed(2)} kr</span>
                </div>
              </div>
            )}

            <div className="psr-summary-card products">
              <div className="psr-card-icon">
                <i className="fas fa-tags"></i>
              </div>
              <div className="psr-card-content">
                <span className="psr-card-label">Unika produkter</span>
                <span className="psr-card-value">{report.summary.unique_products}</span>
              </div>
            </div>
          </div>

          {/* Topprodukter */}
          {report.top_by_revenue.length > 0 && (
            <div className="psr-top-products">
              <h3><i className="fas fa-trophy"></i> Topprodukter (intakt)</h3>
              <div className="psr-top-list">
                {report.top_by_revenue.map((product, index) => (
                  <div key={product.product_id} className="psr-top-item">
                    <span className="psr-top-rank">#{index + 1}</span>
                    <span className="psr-top-name">
                      {product.name}
                      {product.is_friskvard && (
                        <span className="psr-friskvard-badge" title="Friskvard">
                          <i className="fas fa-heart"></i>
                        </span>
                      )}
                    </span>
                    <span className="psr-top-qty">{product.quantity_sold} st</span>
                    <span className="psr-top-amount">{product.total_amount.toFixed(2)} kr</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fullstandig produktlista */}
          <div className="psr-products">
            <h3><i className="fas fa-list"></i> Alla produkter</h3>
            {report.products.length === 0 ? (
              <p className="psr-empty">Inga produkter salda under denna period.</p>
            ) : (
              <div className="psr-table-container">
                <table className="psr-table">
                  <thead>
                    <tr>
                      <th>Produkt</th>
                      <th>Momssats</th>
                      <th>Antal salda</th>
                      <th>Antal forsaljningar</th>
                      <th>Moms</th>
                      <th>Totalt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.products.map(product => (
                      <tr key={product.product_id} className={product.is_friskvard ? 'psr-friskvard-row' : ''}>
                        <td>
                          {product.name}
                          {product.is_friskvard && (
                            <span className="psr-friskvard-badge" title="Friskvard">
                              <i className="fas fa-heart"></i>
                            </span>
                          )}
                        </td>
                        <td>{product.vat_rate}%</td>
                        <td className="psr-number">{product.quantity_sold}</td>
                        <td className="psr-number">{product.sale_count}</td>
                        <td className="psr-number">{product.total_vat.toFixed(2)} kr</td>
                        <td className="psr-number psr-amount">{product.total_amount.toFixed(2)} kr</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2"><strong>Totalt</strong></td>
                      <td className="psr-number"><strong>{report.summary.total_products_sold}</strong></td>
                      <td></td>
                      <td className="psr-number"><strong>{report.summary.total_vat.toFixed(2)} kr</strong></td>
                      <td className="psr-number psr-amount"><strong>{report.summary.total_revenue.toFixed(2)} kr</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="psr-period-info">
            Period: {report.period.from_date} - {report.period.to_date}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductSalesReport;
